import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Check, Clock, FileSearch, History, Loader2, MapPin, Search, UserCheck } from "lucide-react";
import toast from "react-hot-toast";
import api, { errorMessage } from "../services/api";
import { getAdminSocket, useRiderUpdates } from "../services/adminSocket";
import RefreshButton from "./RefreshButton";
import RideTimelineModal from "./RideTimelineModal";
import RiderStatusBadge from "./RiderStatusBadge";
import { DISPATCHABLE, assignability } from "../utils/riderStatus";
import { timeAgo } from "../utils/time";
import { cancellationLine, shortBookingId } from "../utils/tripLog";

const OFFER_LABEL = { notified: "Sent", accepted: "Accepted", ignored: "Ignored", declined: "Declined", expired: "Expired" };

/**
 * Nudge and assign for the ride picked in the live monitor. One rider list,
 * free riders first: tick several to nudge, or assign the ride to one rider with room for it.
 */
function DispatchPanel({ bookingId, waitingCount = 0, fill = false }) {
  const [ride, setRide] = useState(null);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [timelineOpen, setTimelineOpen] = useState(false);
  // The timeline doesn't carry who cancelled or why; the booking does.
  const [cancelInfo, setCancelInfo] = useState({ status: "idle", line: null });
  const [cancelRetry, setCancelRetry] = useState(0);
  const inFlight = useRef(false);
  const latestLoad = useRef(0);
  // The ride on screen now; a reply for any other ride is dropped.
  const currentId = useRef(bookingId);
  currentId.current = bookingId;

  // The ride changes often (socket, poll); the rider directory only on a slower poll or a manual refresh.
  const load = useCallback(async ({ withRiders = false } = {}) => {
    const id = currentId.current;
    if (!id) return;
    const loadId = ++latestLoad.current;
    setLoading(true);
    const [rideRes, ridersRes] = await Promise.allSettled([
      api.get(`/v1/admin/rides/${id}/timeline`),
      withRiders ? api.get("/v1/admin/riders-basic") : Promise.resolve(null),
    ]);
    if (loadId !== latestLoad.current || id !== currentId.current) return;
    if (rideRes.status === "fulfilled") setRide(rideRes.value.data);
    if (withRiders && ridersRes.status === "fulfilled") {
      const list = ridersRes.value?.data?.data;
      setRiders(Array.isArray(list) ? list : []);
    }
    const failed = [rideRes, ridersRes].find((r) => r.status === "rejected");
    // A failed refresh keeps what was already on screen and says so.
    setError(failed ? errorMessage(failed.reason, "Couldn't load this ride's riders.") : null);
    if (!failed) setUpdatedAt(new Date());
    setLoading(false);
  }, []);

  const refreshAll = useCallback(() => load({ withRiders: true }), [load]);
  // A rider edited or suspended elsewhere drops in or out of this list now.
  useRiderUpdates(refreshAll);

  useEffect(() => {
    setRide(null);
    setSelected(new Set());
    setQuery("");
    setError(null);
    if (!bookingId) return undefined;
    refreshAll();
    const socket = getAdminSocket();
    const onUpdate = (p) => {
      if (p?.bookingId === bookingId) load();
    };
    socket?.on("admin:ride_update", onUpdate);
    const rideTimer = setInterval(() => {
      if (!document.hidden) load();
    }, 15000);
    const ridersTimer = setInterval(() => {
      if (!document.hidden) refreshAll();
    }, 60000);
    return () => {
      socket?.off("admin:ride_update", onUpdate);
      clearInterval(rideTimer);
      clearInterval(ridersTimer);
    };
  }, [bookingId, load, refreshAll]);

  const cancelled = ride?.status === "cancelled";
  useEffect(() => {
    if (!cancelled || !bookingId) {
      setCancelInfo({ status: "idle", line: null });
      return undefined;
    }
    let live = true;
    setCancelInfo({ status: "loading", line: null });
    api
      .get(`/v1/admin/bookings/${bookingId}`)
      .then((res) => {
        if (live) setCancelInfo({ status: "ready", line: cancellationLine(res.data?.data || res.data) });
      })
      .catch(() => {
        if (live) setCancelInfo({ status: "error", line: null });
      });
    return () => {
      live = false;
    };
  }, [cancelled, bookingId, cancelRetry]);

  const offers = useMemo(() => {
    const m = new Map();
    for (const o of ride?.dispatchedTo || []) m.set(String(o.driverId), o);
    return m;
  }, [ride]);

  // Ticked riders stay listed even when the search hides them, so a nudge never reaches someone off screen.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return riders.filter(
      (r) =>
        DISPATCHABLE.has(r.dispatchStatus) &&
        (selected.has(String(r._id)) ||
          !q ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.email || "").toLowerCase().includes(q)),
    );
  }, [riders, query, selected]);

  const waiting = ride?.status === "requested";
  const busy = sending || assigningId !== null;

  // Riders who can't fit this booking (on a charter, too few seats) can't be nudged.
  const nudgeable = useMemo(
    () => new Set(riders.filter((r) => assignability(r, ride).canNudge).map((r) => String(r._id))),
    [riders, ride],
  );
  const toNudge = Array.from(selected).filter((id) => nudgeable.has(id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // The ride may have been accepted or cancelled while the admin was choosing.
  const stillWaiting = async (id, action) => {
    const { data: current } = await api.get(`/v1/admin/rides/${id}/timeline`);
    if (current?.status === "requested") return true;
    const now = String(current?.status || "no longer open").replace(/_/g, " ");
    toast.error(`${action} not sent: the ride is now ${now}.`);
    return false;
  };

  const nudge = async () => {
    const id = bookingId;
    const riderIds = toNudge;
    if (riderIds.length === 0 || inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    try {
      if (!(await stillWaiting(id, "Nudge"))) return;
      const res = await api.post(`/v1/admin/rides/${id}/nudge`, { riderIds });
      const n = res.data?.nudged ?? riderIds.length;
      // Riders who can't take the ride (on a charter, not enough seats) are skipped by the server.
      const skipped = (res.data?.results || []).filter((r) => !r.ok && r.reason);
      if (n > 0) toast.success(`Nudged ${n} rider${n === 1 ? "" : "s"}`);
      skipped.forEach((r) => toast.error(`Not nudged: ${r.reason}.`, { id: `nudge-skip-${id}-${r.riderId}` }));
      if (id === currentId.current) setSelected(new Set());
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't nudge riders. Please try again."));
    } finally {
      inFlight.current = false;
      setSending(false);
      if (id === currentId.current) load();
    }
  };

  const assign = async (rider) => {
    const id = bookingId;
    // The ref blocks a second click before React re-renders the disabled button.
    if (inFlight.current) return;
    inFlight.current = true;
    setAssigningId(String(rider._id));
    try {
      if (!(await stillWaiting(id, "Assignment"))) return;
      const res = await api.post(`/v1/admin/bookings/${id}/assign/${rider._id}`);
      toast.success(res.data?.message || `Ride assigned to ${rider.name}`, { id: `assign-${id}` });
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't assign the ride. Please try again."), { id: `assign-${id}` });
    } finally {
      inFlight.current = false;
      setAssigningId(null);
      if (id === currentId.current) refreshAll();
    }
  };

  const card = fill
    ? "bg-white dark:bg-gray-900 flex flex-col h-full"
    : "bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex flex-col h-[560px]";

  if (!bookingId) {
    return (
      <div className={card}>
        <div className="p-5 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Dispatch</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <UserCheck className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Pick a ride to nudge or assign it</p>
          <p className="text-xs text-gray-400 mt-1">
            {waitingCount > 0
              ? `${waitingCount} ride${waitingCount === 1 ? " is" : "s are"} waiting for a rider.`
              : "No rides are waiting for a rider right now."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={card}>
      {/* Header: the ride this panel acts on */}
      <div className="p-5 border-b border-gray-100 dark:border-gray-800 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Dispatch</h3>
          <RefreshButton onRefresh={refreshAll} loading={loading} updatedAt={updatedAt} />
        </div>
        {ride ? (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {ride.passenger?.name || "Passenger"}
                <span className="ml-2 font-mono text-xs font-normal text-gray-400">{shortBookingId(bookingId)}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {ride.pickupLocationName} → {ride.dropoffLocationName}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setTimelineOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#FF8C00]"
              >
                <History className="w-3.5 h-3.5" />
                Timeline
              </button>
              <Link
                to={`/trip-inspector/${bookingId}`}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#FF8C00]"
              >
                <FileSearch className="w-3.5 h-3.5" />
                Inspect
              </Link>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Loading ride…</p>
        )}
        {ride && !waiting ? (
          <p className="text-xs rounded-lg bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-gray-200 px-3 py-2">
            This ride is {String(ride.status).replace(/_/g, " ")}, so nudging and assigning are off.
            {cancelled ? (
              <span className="block mt-1 text-slate-700 dark:text-gray-100">
                {cancelInfo.line ||
                  (cancelInfo.status === "error" ? (
                    <>
                      {"Couldn't load why this ride was cancelled. "}
                      <button type="button" onClick={() => setCancelRetry((n) => n + 1)} className="underline font-medium">
                        Retry
                      </button>
                    </>
                  ) : cancelInfo.status === "ready"
                      ? null
                      : "Loading why it was cancelled…")}
              </span>
            ) : null}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 px-3 py-2">
            {error}{" "}
            <button type="button" onClick={refreshAll} className="underline font-medium">
              Retry
            </button>
          </p>
        ) : null}
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search riders…"
            aria-label="Search riders"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FFD36980]"
          />
        </div>
      </div>

      {/* Riders, free first */}
      <div className="flex-1 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/60">
        {waiting && riders.length > 0 ? (
          <p className="px-5 py-2 text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50/60 dark:bg-gray-800/40">
            Assign works for riders with enough free seats. Tick a rider to nudge them.
          </p>
        ) : null}
        {riders.length === 0 && loading ? (
          <div className="p-8 text-center text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
            Loading riders…
          </div>
        ) : visible.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-400">
            {query ? "No riders match." : "No approved riders."}
          </p>
        ) : (
          visible.map((r) => {
            const id = String(r._id);
            const offer = offers.get(id);
            const isSel = selected.has(id) && nudgeable.has(id);
            const { canAssign, canNudge, reason } = assignability(r, ride);
            const lastSeen = timeAgo(r.lastSeenAt);
            return (
              <div key={id} className={`flex items-center gap-3 px-5 py-2.5 ${isSel ? "bg-orange-50/60 dark:bg-orange-500/5" : ""}`}>
                <label
                  className={`flex items-center gap-3 flex-1 min-w-0 ${canNudge ? "cursor-pointer" : "cursor-default"}`}
                  title={canNudge || !waiting ? undefined : `Can't nudge: ${reason}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggle(id)}
                    disabled={!canNudge}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      isSel ? "bg-[#FF8C00] border-[#FF8C00]" : "border-gray-300 dark:border-gray-600"
                    } ${canNudge ? "" : "opacity-40"}`}
                  >
                    {isSel ? <Check className="w-3 h-3 text-white" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-900 dark:text-white truncate">{r.name || "Unnamed rider"}</span>
                    <span className="flex items-center gap-2 mt-0.5">
                      <RiderStatusBadge status={r.dispatchStatus} alwaysOnline={!!r.alwaysOnline} rider={r} />
                      {offer ? (
                        <span className="text-[10px] text-gray-400">
                          {offer.nudgeCount ? `nudged ${offer.nudgeCount}×` : OFFER_LABEL[offer.status] || offer.status}
                        </span>
                      ) : null}
                      {lastSeen ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400">
                          <Clock className="w-2.5 h-2.5" />
                          {lastSeen}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => assign(r)}
                  disabled={!canAssign || busy}
                  title={canAssign ? `Give this ride to ${r.name}` : reason}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-[#FF8C00] text-[#FF8C00] hover:bg-orange-50 dark:hover:bg-orange-500/10 disabled:border-gray-200 disabled:text-gray-300 dark:disabled:border-gray-700 dark:disabled:text-gray-600 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  {assigningId === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {assigningId === id ? "Assigning…" : "Assign"}
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Nudge the ticked riders */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
        <span className="text-xs text-gray-500">
          {toNudge.length ? `${toNudge.length} selected` : "Tick riders to nudge them"}
        </span>
        <button
          type="button"
          onClick={nudge}
          disabled={!waiting || toNudge.length === 0 || busy}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-[#FF8C00] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e67e00] transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
          Nudge{toNudge.length ? ` ${toNudge.length}` : ""}
        </button>
      </div>

      {timelineOpen ? <RideTimelineModal bookingId={bookingId} onClose={() => setTimelineOpen(false)} /> : null}
    </div>
  );
}

export default DispatchPanel;
